import { getCsrfToken } from "@/integrations/php/client";

export type PartnerCareRange = "30d" | "90d" | "all";
export type PartnerCareStatus = "all" | "delivered" | "engaged" | "not-opened" | "failed" | "dashboard-active" | "not-activated";

export interface PartnerCarePartner {
  id: string;
  name: string | null;
  company_name: string | null;
  email: string | null;
  digest_count: number;
  sent_count: number;
  failed_count: number;
  pending_count: number;
  last_sent_at: string | null;
  last_failed_at: string | null;
  detected_open_count: number;
  lead_click_count: number;
  lead_click_total: number;
  dashboard_click_count: number;
  last_open_at: string | null;
  last_lead_click_at: string | null;
  last_dashboard_click_at: string | null;
  password_set_at: string | null;
  last_login: string | null;
  invite_delivered_at: string | null;
  invite_opened_at: string | null;
  setup_requested_at: string | null;
  credential_created_at: string | null;
  first_login_at: string | null;
  dashboard_active: boolean;
}

export interface PartnerCareDeliveryItem {
  notification_job_id: number;
  lead_id: string;
  lead_name: string;
  service: string;
  stage: string;
  stage_snapshot: string;
  lead_created_at: string;
  reminder_sequence: number;
  reminder_status: string;
  clicked: boolean;
  click_count: number;
  first_click_at: string | null;
  last_click_at: string | null;
}

export interface PartnerCareDelivery {
  id: number;
  partner_id: string;
  partner_name: string | null;
  partner_email: string | null;
  status: string;
  attempts: number;
  max_attempts: number;
  last_error_code: string | null;
  scheduled_at: string | null;
  sent_at: string | null;
  updated_at: string | null;
  item_count: number;
  detected_open_count: number;
  lead_click_count: number;
  lead_click_total: number;
  dashboard_click_count: number;
  last_open_at: string | null;
  last_click_at: string | null;
  items: PartnerCareDeliveryItem[];
}

export interface PartnerCareResponse {
  generated_at: string;
  settings: {
    enabled: boolean;
    activated_at: string | null;
    first_after_hours: number;
    final_after_hours: number;
    daily_send_time: string;
  };
  summary: {
    jobs: { total: number; sent: number; pending: number; failed: number; cancelled: number; partners_reached: number };
    events: { detected_opens: number; lead_clicks: number; lead_click_total: number; dashboard_clicks: number; partners_opened: number; partners_clicked: number };
    portal: { active_partners: number; credentials_created: number; ever_logged_in: number; logged_in_30d: number };
    onboarding: { invite_delivered: number; invite_opened: number; setup_requested: number; credential_created: number; first_login: number };
  };
  partners: PartnerCarePartner[];
  recent_deliveries: PartnerCareDelivery[];
  pagination: { page: number; per_page: number; total: number; pages: number };
  filters: { range: PartnerCareRange; status: PartnerCareStatus; q: string };
}

export async function fetchPartnerCare(input: {
  range: PartnerCareRange;
  status: PartnerCareStatus;
  search: string;
  page: number;
}): Promise<PartnerCareResponse> {
  const params = new URLSearchParams({
    range: input.range,
    status: input.status,
    page: String(input.page),
    per_page: "20",
  });
  if (input.search.trim()) params.set("q", input.search.trim());
  const response = await fetch(`/api/admin/partner-care.php?${params.toString()}`, {
    credentials: "include",
    headers: { "X-CSRF-Token": await getCsrfToken() },
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body?.error?.message || "Τα δεδομένα Partner Care δεν φορτώθηκαν.");
  return body.data as PartnerCareResponse;
}
